import { Button } from "@/components/ui/button";
import FilloutButton from "@/components/FilloutButton";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, Star, Clock, TrendingUp, Code } from "lucide-react";
import { useTranslation } from "react-i18next";
import danielHeadshot from "@/assets/daniel-headshot.jpg";
import TrustSignals from "./TrustSignals";

const HeroSection = () => {
  const { t, ready } = useTranslation('home');
  
  if (!ready) {
    return (
      <section className="bg-gradient-hero py-20 relative overflow-hidden min-h-[60vh] flex items-center">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="animate-pulse space-y-4">
              <div className="h-8 bg-primary-foreground/20 rounded w-3/4 mx-auto"></div>
              <div className="h-12 bg-primary-foreground/20 rounded w-1/2 mx-auto"></div>
              <div className="h-6 bg-primary-foreground/20 rounded w-2/3 mx-auto"></div>
            </div>
          </div>
        </div>
      </section>
    );
  }
  
  return (
    <section className="bg-gradient-hero py-20 relative overflow-hidden">
      <div className="absolute top-20 left-10 w-32 h-32 bg-accent/10 rounded-full blur-3xl animate-float"></div>
      <div className="absolute bottom-20 right-10 w-48 h-48 bg-golden-primary/10 rounded-full blur-3xl animate-float" style={{animationDelay: '1s'}}></div>
      
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto text-center">
          {/* Profile intro */}
          <div className="flex flex-col sm:flex-row items-center justify-center mb-8 animate-fade-in-up group">
            <div className="relative mb-4 sm:mb-0">
              <div className="absolute inset-0 bg-gradient-to-br from-accent/20 to-golden-primary/20 rounded-full blur-xl scale-110"></div>
              <img 
                src="/images/daniel.png"
                alt="Daniel Gadus - No-Code Expert"
                className="relative w-20 h-20 sm:w-24 sm:h-24 rounded-full border-4 border-accent shadow-golden group-hover:scale-105 transition-elegant object-cover object-center"
              />
              <div className="absolute -bottom-2 -right-2 bg-accent rounded-full p-2 animate-float">
                <CheckCircle className="w-4 h-4 text-accent-foreground" />
              </div>
            </div>
            <div className="sm:ml-6 text-center sm:text-left">
              <div className="text-accent text-lg sm:text-xl font-semibold">{t('hero.greeting')}</div>
              <p className="text-primary-foreground/80 text-sm max-w-sm">
                {t('hero.personalDescription')}
              </p>
            </div>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-primary-foreground mb-6 leading-tight animate-fade-in-up" style={{animationDelay: '0.2s'}}>
            {t('hero.title')}
          </h1>
          
          <p className="text-lg sm:text-xl md:text-2xl text-accent font-semibold mb-8 animate-fade-in-up" style={{animationDelay: '0.3s'}}>
            {t('hero.subtitle')}
          </p>
          
          <p className="text-base sm:text-lg text-primary-foreground/90 mb-12 max-w-3xl mx-auto leading-relaxed animate-fade-in-up px-4" style={{animationDelay: '0.4s'}}>
            {t('hero.description')}
          </p>

          {/* Feature cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 mb-12 animate-fade-in-up px-4" style={{animationDelay: '0.5s'}}>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-white/20 hover:bg-white/15 hover:scale-105 transition-elegant hover:shadow-golden group">
              <div className="flex items-center justify-center w-12 h-12 bg-accent rounded-lg mb-4 mx-auto group-hover:scale-110 transition-elegant">
                <Clock className="w-6 h-6 text-accent-foreground" />
              </div>
              <div className="text-primary-foreground font-semibold mb-2 text-sm sm:text-base">{t('features.saveTime.title')}</div>
              <p className="text-primary-foreground/80 text-xs sm:text-sm">
                {t('features.saveTime.description')}
              </p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-white/20 hover:bg-white/15 hover:scale-105 transition-elegant hover:shadow-golden group">
              <div className="flex items-center justify-center w-12 h-12 bg-accent rounded-lg mb-4 mx-auto group-hover:scale-110 transition-elegant">
                <TrendingUp className="w-6 h-6 text-accent-foreground" />
              </div>
              <div className="text-primary-foreground font-semibold mb-2 text-sm sm:text-base">{t('features.increaseSales.title')}</div>
              <p className="text-primary-foreground/80 text-xs sm:text-sm">
                {t('features.increaseSales.description')}
              </p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-white/20 hover:bg-white/15 hover:scale-105 transition-elegant hover:shadow-golden group sm:col-span-2 md:col-span-1">
              <div className="flex items-center justify-center w-12 h-12 bg-accent rounded-lg mb-4 mx-auto group-hover:scale-110 transition-elegant">
                <Code className="w-6 h-6 text-accent-foreground" />
              </div>
              <div className="text-primary-foreground font-semibold mb-2 text-sm sm:text-base">{t('features.noCoding.title')}</div>
              <p className="text-primary-foreground/80 text-xs sm:text-sm">
                {t('features.noCoding.description')}
              </p>
            </div>
          </div>

          <div className="animate-fade-in-up px-4" style={{animationDelay: '0.6s'}}>
            <FilloutButton size="lg" variant="secondary" className="mb-8 text-base sm:text-lg px-6 sm:px-8 py-3 hover:scale-105 transition-elegant shadow-golden hover:shadow-xl w-full sm:w-auto">
              {t('hero.cta')}
            </FilloutButton>
            
            <p className="text-primary-foreground/60 text-sm mb-8">
              {t('hero.noCommitment')}
            </p>
          </div>

          {/* Social proof */}
          <div className="animate-fade-in-up" style={{animationDelay: '0.7s'}}>
            <div className="flex items-center justify-center space-x-4 sm:space-x-8 text-center">
              <div className="flex items-center space-x-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 sm:w-5 sm:h-5 fill-accent text-accent hover:scale-110 transition-elegant" style={{animationDelay: `${0.8 + i * 0.1}s`}} />
                ))}
              </div>
            </div>
            
            <div className="flex items-center justify-center space-x-2 sm:space-x-4 md:space-x-8 mt-4 flex-wrap gap-2 px-4">
              <Badge variant="secondary" className="bg-white/20 text-primary-foreground border-white/30 hover:bg-white/30 transition-elegant">
                {t('socialProof.clients')}
              </Badge>
              <Badge variant="secondary" className="bg-white/20 text-primary-foreground border-white/30 hover:bg-white/30 transition-elegant">
                {t('socialProof.rating')}
              </Badge>
              <Badge variant="secondary" className="bg-white/20 text-primary-foreground border-white/30 hover:bg-white/30 transition-elegant">
                {t('socialProof.satisfaction')}
              </Badge>
            </div>
          </div>
          
          {/* Trust Signals */}
          <div className="mt-12">
            <TrustSignals />
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;